import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Square, Camera, Wifi, Monitor, AlertTriangle, CheckCircle2, Loader, Video, Search } from 'lucide-react';
import '../App.css';
import {
  uploadImage,
  formatDetectionError,
  getApiBaseUrl,
  startStream,
  stopStream,
  getFrame,
  discoverCameras,
  analyzeSuspiciousActivity,
} from '../utils/api';
import { getSelectedObjects } from '../utils/detectionSettings';

// ─── Mode constants ───────────────────────────────────────────────────────────
const MODE_WEBCAM = 'webcam';
const MODE_LOCAL_VIDEO = 'local_video';
const MODE_MJPEG  = 'mjpeg';
const MODE_RTSP   = 'rtsp';

// How many ms between frames sent to the backend
const FRAME_INTERVAL_MS = 800; // ~1.25 fps — keeps Render free tier alive
const ACTIVITY_ANALYSIS_FRAME_INTERVAL = 10;
const ACTIVITY_ANALYSIS_MIN_INTERVAL_MS = 30000;

export default function StreamProcessor() {
  const [mode, setMode]             = useState(MODE_WEBCAM);
  const [mjpegUrl, setMjpegUrl]     = useState('');
  const [rtspUrl, setRtspUrl]       = useState('');
  const [cameraName, setCameraName] = useState('My Camera');
  const [isStreaming, setIsStreaming] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [message, setMessage]         = useState(null);

  const [detections, setDetections]         = useState(null);
  const [backendFrame, setBackendFrame]     = useState(null);
  const [stats, setStats]                   = useState({ frames: 0, detectedTotal: 0, fps: 0 });
  const [processingFrame, setProcessingFrame] = useState(false);
  const [webcamReady, setWebcamReady]       = useState(false);
  const [securityInsight, setSecurityInsight] = useState(null);
  const [discoveringCameras, setDiscoveringCameras] = useState(false);
  const [discoveredCameras, setDiscoveredCameras] = useState([]);
  const [selectedDiscoveredSource, setSelectedDiscoveredSource] = useState('');
  const [discoveryMessage, setDiscoveryMessage] = useState('');

  const [localVideoUrl, setLocalVideoUrl]   = useState('');

  const videoRef    = useRef(null);   // <video> element for webcam
  const canvasRef   = useRef(null);   // off-screen canvas for frame capture
  const overlayCanvasRef = useRef(null); // visible canvas for detection boxes
  const imgRef      = useRef(null);   // <img> for MJPEG display
  const intervalRef = useRef(null);
  const streamRef   = useRef(null);   // MediaStream from getUserMedia
  const backendStreamIdRef = useRef(null);
  const fpsTimer    = useRef(null);
  const frameCountRef = useRef(0);
  const processingFrameRef = useRef(false);
  const activityLogsRef = useRef([]);
  const lastActivityAnalysisRef = useRef(0);

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (localVideoUrl) {
        URL.revokeObjectURL(localVideoUrl);
      }
      setLocalVideoUrl(URL.createObjectURL(file));
      setError(null);
      setWebcamReady(false);
    }
  };

  const handleDiscoverCameras = async () => {
    setError(null);
    setMessage(null);
    setDiscoveryMessage('');
    setDiscoveredCameras([]);
    setSelectedDiscoveredSource('');
    setDiscoveringCameras(true);

    try {
      const result = await discoverCameras({ timeout: 0.25, auto_save: true });
      const cameras = result?.cameras || [];
      setDiscoveredCameras(cameras);

      if (result?.warning) {
        setDiscoveryMessage(result.warning);
        return;
      }

      setDiscoveryMessage(
        cameras.length
          ? `Found ${cameras.length} possible camera stream${cameras.length === 1 ? '' : 's'} on this Wi-Fi network.`
          : 'No camera streams found. Check that the camera is powered on and connected to the same Wi-Fi/LAN as the local backend.'
      );
    } catch (err) {
      setDiscoveryMessage('');
      setError(formatDetectionError(err));
    } finally {
      setDiscoveringCameras(false);
    }
  };

  const handleSelectDiscoveredCamera = (camera) => {
    const source = camera?.source || '';
    if (!source) return;

    if (source.startsWith('rtsp://')) {
      setMode(MODE_RTSP);
      setRtspUrl(source);
    } else {
      setMode(MODE_MJPEG);
      setMjpegUrl(source);
    }

    setCameraName(camera.name || `Camera ${camera.host || ''}`.trim());
    setSelectedDiscoveredSource(source);
    setError(null);
    setMessage('Camera stream selected. Start detection when ready.');
  };

  const handleDiscoveredCameraChange = (source) => {
    setSelectedDiscoveredSource(source);
    const camera = discoveredCameras.find(item => item.source === source);
    if (camera) {
      handleSelectDiscoveredCamera(camera);
    }
  };

  const selectedObjects    = getSelectedObjects();
  const personEnabled      = selectedObjects.includes('person');

  // ─── FPS tracking ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isStreaming) {
      fpsTimer.current = setInterval(() => {
        setStats(s => ({ ...s, fps: frameCountRef.current }));
        frameCountRef.current = 0;
      }, 1000);
    }
    return () => clearInterval(fpsTimer.current);
  }, [isStreaming]);

  const maybeAnalyzeActivity = useCallback(async (nextLog) => {
    activityLogsRef.current = [...activityLogsRef.current.slice(-49), nextLog];

    const framesSinceLastCheck = nextLog.frame_number % ACTIVITY_ANALYSIS_FRAME_INTERVAL === 0;
    const enoughTimeElapsed = Date.now() - lastActivityAnalysisRef.current >= ACTIVITY_ANALYSIS_MIN_INTERVAL_MS;
    if (!framesSinceLastCheck || !enoughTimeElapsed) return;

    lastActivityAnalysisRef.current = Date.now();
    try {
      const result = await analyzeSuspiciousActivity(
        activityLogsRef.current,
        `Source: ${nextLog.source}. Review recent object detections for suspicious retail activity.`
      );
      setSecurityInsight(result);
    } catch (err) {
      console.warn('[stream] suspicious activity analysis error:', err.message);
      setSecurityInsight({ success: false, error: formatDetectionError(err) });
    }
  }, []);

  const recordActivityLog = useCallback((source, result, frameNumber) => {
    const detectionsList = result?.detections || [];
    const classCounts = detectionsList.reduce((counts, detection) => {
      counts[detection.class] = (counts[detection.class] || 0) + 1;
      return counts;
    }, {});

    const logEntry = {
      timestamp: new Date().toISOString(),
      source,
      frame_number: frameNumber,
      total_detections: detectionsList.length,
      class_counts: classCounts,
      detections: detectionsList.map(detection => ({
        class: detection.class,
        confidence: detection.confidence,
        bbox: detection.bbox,
      })),
    };

    maybeAnalyzeActivity(logEntry);
  }, [maybeAnalyzeActivity]);

  // ─── Capture + detect a single frame ─────────────────────────────────────
  const captureAndDetect = useCallback(async () => {
    if (processingFrameRef.current) return;

    const canvas = canvasRef.current;
    const ctx    = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const overlayCanvas = overlayCanvasRef.current;
    const overlayCtx = overlayCanvas?.getContext('2d');

    // For webcam or local video: draw from the <video> element
    if (mode === MODE_WEBCAM || mode === MODE_LOCAL_VIDEO) {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      if (mode === MODE_LOCAL_VIDEO && (video.paused || video.ended)) return;
      canvas.width  = video.videoWidth  || 640;
      canvas.height = video.videoHeight || 480;
      if (overlayCanvas) {
        overlayCanvas.width = canvas.width;
        overlayCanvas.height = canvas.height;
      }
      ctx.drawImage(video, 0, 0);
    }

    // For MJPEG: draw from the <img> element
    if (mode === MODE_MJPEG) {
      const img = imgRef.current;
      if (!img || !img.complete || img.naturalWidth === 0) return;
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      if (overlayCanvas) {
        overlayCanvas.width = canvas.width;
        overlayCanvas.height = canvas.height;
      }
      ctx.drawImage(img, 0, 0);
    }

    if (overlayCtx && overlayCanvas) {
      overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    }

    // Convert canvas to Blob → File
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      processingFrameRef.current = true;
      setProcessingFrame(true);
      try {
        const file = new File([blob], 'frame.jpg', { type: 'image/jpeg' });
        const result = await uploadImage(file, 0.4, selectedObjects);

        frameCountRef.current += 1;
        recordActivityLog(mode === MODE_WEBCAM ? 'webcam' : 'local-video', result, frameCountRef.current);
        setStats(s => ({ ...s, frames: s.frames + 1, detectedTotal: s.detectedTotal + (result.count || 0) }));

        // Draw bounding boxes on the transparent overlay canvas.
        if (overlayCtx && result.detections?.length) {
          result.detections.forEach(det => {
            const [x1, y1, x2, y2] = det.bbox;
            overlayCtx.strokeStyle = '#22d3ee';
            overlayCtx.lineWidth   = 2;
            overlayCtx.shadowColor = 'rgba(34,211,238,0.5)';
            overlayCtx.shadowBlur  = 6;
            overlayCtx.strokeRect(x1, y1, x2 - x1, y2 - y1);
            overlayCtx.shadowBlur = 0;

            // Label
            const label = `${det.class} ${(det.confidence * 100).toFixed(0)}%`;
            overlayCtx.font = '12px sans-serif';
            overlayCtx.fillStyle = 'rgba(34,211,238,0.85)';
            overlayCtx.fillRect(x1, Math.max(0, y1 - 18), overlayCtx.measureText(label).width + 8, 18);
            overlayCtx.fillStyle = '#0f172a';
            overlayCtx.fillText(label, x1 + 4, Math.max(12, y1 - 4));
          });
        }

        setDetections(result);
      } catch (err) {
        console.warn('[stream] frame detection error:', err.message);
        // Don't set error state on every frame — just log it
      } finally {
        processingFrameRef.current = false;
        setProcessingFrame(false);
      }
    }, 'image/jpeg', 0.8);
  }, [mode, selectedObjects, recordActivityLog]);

  const pollBackendFrame = useCallback(async (streamId) => {
    if (processingFrameRef.current) return;

    processingFrameRef.current = true;
    setProcessingFrame(true);
    try {
      const result = await getFrame(streamId);
      const frameDetections = result.detections?.detections || [];
      const nextFrameNumber = frameCountRef.current + 1;
      setBackendFrame(`data:image/jpeg;base64,${result.frame}`);
      setDetections({
        success: true,
        detections: frameDetections,
        count: frameDetections.length,
        timestamp: new Date().toISOString(),
      });
      frameCountRef.current = nextFrameNumber;
      recordActivityLog(mode === MODE_RTSP ? 'rtsp' : 'wifi-camera', { detections: frameDetections }, nextFrameNumber);
      setStats(s => ({
        frames: s.frames + 1,
        detectedTotal: s.detectedTotal + frameDetections.length,
        fps: s.fps,
      }));
    } catch (err) {
      console.warn('[stream] backend frame error:', err.message);
    } finally {
      processingFrameRef.current = false;
      setProcessingFrame(false);
    }
  }, [mode, recordActivityLog]);

  // ─── Start streaming ──────────────────────────────────────────────────────
  const handleStart = async () => {
    setError(null);
    setMessage(null);
    setDetections(null);
    setBackendFrame(null);
    setWebcamReady(false);
    setSecurityInsight(null);
    activityLogsRef.current = [];
    lastActivityAnalysisRef.current = 0;
    setStats({ frames: 0, detectedTotal: 0, fps: 0 });
    frameCountRef.current = 0;
    processingFrameRef.current = false;
    const overlayCanvas = overlayCanvasRef.current;
    const overlayCtx = overlayCanvas?.getContext('2d');
    if (overlayCanvas && overlayCtx) {
      overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    }
    setLoading(true);

    try {
      if (mode === MODE_WEBCAM) {
        setIsStreaming(true);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        streamRef.current = stream;
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;
          await videoRef.current.play();
          setWebcamReady(videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0);
        }
      }

      if (mode === MODE_LOCAL_VIDEO) {
        if (!localVideoUrl) {
          setError('Please select a local video file first.');
          setLoading(false);
          return;
        }
        setIsStreaming(true);
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (videoRef.current) {
          videoRef.current.src = localVideoUrl;
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;
          await videoRef.current.play();
          setWebcamReady(videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0);
        }
      }

      if (mode === MODE_MJPEG) {
        if (!mjpegUrl.trim()) {
          setError('Please enter the camera stream URL (e.g. http://192.168.1.100:8080/video).');
          setLoading(false);
          return;
        }
        const streamId = `wifi_${Date.now()}`;
        await startStream(streamId, mjpegUrl.trim(), selectedObjects, {
          camera_name: cameraName,
          camera_type: 'wifi',
          save_camera: Boolean(cameraName.trim()),
        });
        backendStreamIdRef.current = streamId;
      }

      if (mode === MODE_RTSP) {
        if (!rtspUrl.trim()) {
          setError('Please enter the RTSP camera URL (e.g. rtsp://192.168.1.100:554/stream).');
          setLoading(false);
          return;
        }
        const streamId = `rtsp_${Date.now()}`;
        await startStream(streamId, rtspUrl.trim(), selectedObjects, {
          camera_name: cameraName,
          camera_type: 'rtsp',
          save_camera: Boolean(cameraName.trim()),
        });
        backendStreamIdRef.current = streamId;
      }

      if (mode !== MODE_WEBCAM && mode !== MODE_LOCAL_VIDEO) {
        setIsStreaming(true);
      }
      setMessage(`Streaming started — detecting every ${(FRAME_INTERVAL_MS / 1000).toFixed(1)}s`);
      if (mode === MODE_WEBCAM || mode === MODE_LOCAL_VIDEO) {
        intervalRef.current = setInterval(captureAndDetect, FRAME_INTERVAL_MS);
      } else {
        intervalRef.current = setInterval(
          () => pollBackendFrame(backendStreamIdRef.current),
          FRAME_INTERVAL_MS
        );
        pollBackendFrame(backendStreamIdRef.current);
      }
    } catch (err) {
      setIsStreaming(false);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Make sure a webcam is connected.');
      } else {
        setError(formatDetectionError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Stop streaming ───────────────────────────────────────────────────────
  const handleStop = () => {
    clearInterval(intervalRef.current);
    if (backendStreamIdRef.current) {
      stopStream(backendStreamIdRef.current).catch(err => {
        console.warn('[stream] stop backend stream error:', err.message);
      });
      backendStreamIdRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      if (mode === MODE_LOCAL_VIDEO) {
        videoRef.current.currentTime = 0;
      } else {
        videoRef.current.src = '';
      }
    }
    setBackendFrame(null);
    setWebcamReady(false);
    processingFrameRef.current = false;
    const overlayCanvas = overlayCanvasRef.current;
    const overlayCtx = overlayCanvas?.getContext('2d');
    if (overlayCanvas && overlayCtx) {
      overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    }
    setIsStreaming(false);
    setMessage('Stream stopped.');
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      if (backendStreamIdRef.current) {
        stopStream(backendStreamIdRef.current).catch(() => {});
      }
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (localVideoUrl) {
        URL.revokeObjectURL(localVideoUrl);
      }
    };
  }, [localVideoUrl]);

  const handleWebcamMetadata = () => {
    const video = videoRef.current;
    if (!video) return;

    setWebcamReady(video.videoWidth > 0 && video.videoHeight > 0);
    video.play().catch(err => {
      console.warn('[stream] webcam play retry failed:', err.message);
    });
  };

  // ─── UI ──────────────────────────────────────────────────────────────────
  const modeTab = (id, icon, label, sublabel) => (
    <button
      key={id}
      onClick={() => { setMode(id); setError(null); }}
      disabled={isStreaming}
      className={`flex min-h-[5.5rem] min-w-[8.25rem] flex-1 flex-col items-center justify-center gap-1 rounded-lg border px-3 py-3 text-center text-sm font-medium transition-all
        ${mode === id
          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-300'
          : 'border-slate-600 bg-slate-800/50 text-gray-400 hover:border-slate-500 hover:text-gray-200'}
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {icon}
      <span>{label}</span>
      <span className="text-[10px] opacity-60">{sublabel}</span>
    </button>
  );

  const queueCount  = detections?.detections?.filter(d => d.class === 'person').length ?? 0;
  const classCounts = detections?.detections?.reduce((acc, d) => {
    acc[d.class] = (acc[d.class] || 0) + 1; return acc;
  }, {}) ?? {};

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-white sm:text-3xl">Live Stream Detection</h1>

      {/* ── Mode selector ── */}
      <div className={`glass-panel ${isStreaming ? 'p-3' : 'p-5'}`}>
        <h3 className={`text-sm font-semibold text-gray-400 uppercase tracking-wider ${isStreaming ? 'mb-2' : 'mb-3'}`}>Capture Mode</h3>
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 lg:overflow-visible">
          {modeTab(MODE_WEBCAM, <Monitor className="w-5 h-5" />, 'Webcam',  'Browser camera')}
          {modeTab(MODE_LOCAL_VIDEO, <Video className="w-5 h-5" />, 'Local Video', 'Video file upload')}
          {modeTab(MODE_MJPEG,  <Wifi    className="w-5 h-5" />, 'WiFi IP Camera', 'MJPEG/HTTP stream')}
          {modeTab(MODE_RTSP,   <Camera  className="w-5 h-5" />, 'RTSP Camera', 'Local backend only')}
        </div>

        {/* Mode-specific config */}
        {!isStreaming && (
        <div className="mt-4 space-y-3">
          {(mode === MODE_MJPEG || mode === MODE_RTSP) && (
            <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">Local Wi-Fi camera scan</p>
                  <p className="mt-1 text-sm text-gray-400">
                    The browser cannot inspect Wi-Fi directly. It asks your local backend at <code className="break-all text-cyan-300">{getApiBaseUrl()}</code> to scan the same network.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDiscoverCameras}
                  disabled={discoveringCameras}
                  className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto sm:shrink-0"
                >
                  {discoveringCameras
                    ? <Loader className="h-4 w-4 animate-spin" />
                    : <Search className="h-4 w-4" />}
                  <span>{discoveringCameras ? 'Scanning...' : 'Scan Wi-Fi'}</span>
                </button>
              </div>

              {discoveryMessage && (
                <div className="mt-3 rounded-lg border border-slate-700 bg-slate-800/70 px-3 py-2 text-sm text-gray-300">
                  {discoveryMessage}
                </div>
              )}

              {discoveredCameras.length > 0 && (
                <div className="mt-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-300" htmlFor="discovered-camera-select">
                    Select discovered camera
                  </label>
                  <select
                    id="discovered-camera-select"
                    value={selectedDiscoveredSource}
                    onChange={e => handleDiscoveredCameraChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                  >
                    <option value="">Choose a camera stream...</option>
                    {discoveredCameras.map((camera, index) => (
                      <option key={camera.source} value={camera.source}>
                        {camera.name || `Wi-Fi Camera ${index + 1}`} - {camera.host}:{camera.port} - {camera.source.startsWith('rtsp://') ? 'RTSP' : 'HTTP/MJPEG'}
                      </option>
                    ))}
                  </select>
                  {selectedDiscoveredSource && (
                    <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-cyan-200">Selected stream</p>
                      <p className="mt-1 break-all font-mono text-xs text-cyan-100">{selectedDiscoveredSource}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {mode === MODE_WEBCAM && (
            <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
              <div className="text-sm text-green-200">
                <p className="font-semibold">Works with cloud backend ✓</p>
                <p className="opacity-80 mt-1">Your browser captures frames from your webcam and sends them to the Render backend for YOLO detection. No local server needed.</p>
              </div>
            </div>
          )}

          {mode === MODE_LOCAL_VIDEO && (
            <div className="space-y-3">
              <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                <div className="text-sm text-green-200">
                  <p className="font-semibold">Works with cloud backend ✓</p>
                  <p className="opacity-80 mt-1">Select a local video file. Your browser plays the video locally, extracts frames, and sends them to the backend for real-time YOLO detection.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Select Video File
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoFileChange}
                  disabled={isStreaming}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20"
                />
              </div>
            </div>
          )}

          {mode === MODE_MJPEG && (
            <div className="space-y-3">
              <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                <div className="text-sm text-cyan-200">
                  <p className="font-semibold">Best with local backend</p>
                  <p className="opacity-80 mt-1">The local Python backend connects to the camera on your WiFi network and sends processed frames back to the browser. Render cannot reach private 192.168.x.x camera URLs.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Camera Stream URL <span className="text-gray-500 font-normal">(HTTP / MJPEG)</span>
                </label>
                <input
                  type="text"
                  value={mjpegUrl}
                  onChange={e => setMjpegUrl(e.target.value)}
                  disabled={isStreaming}
                  placeholder="http://192.168.1.100:8080/video  or  http://192.168.1.100/mjpeg"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 disabled:opacity-50 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Common patterns: <code className="text-cyan-400">/video</code>, <code className="text-cyan-400">/mjpeg</code>, <code className="text-cyan-400">/stream</code>, <code className="text-cyan-400">/videostream.cgi</code>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Camera Name</label>
                <input
                  type="text"
                  value={cameraName}
                  onChange={e => setCameraName(e.target.value)}
                  disabled={isStreaming}
                  placeholder="Front Gate Camera"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {mode === MODE_RTSP && (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-200">
                  <p className="font-semibold">Requires local backend</p>
                  <p className="opacity-80 mt-1">RTSP is handled by OpenCV in the local Python backend. Keep this app connected to <code>http://localhost:5000/api</code>; Render cannot access your LAN camera.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  RTSP Stream URL
                </label>
                <input
                  type="text"
                  value={rtspUrl}
                  onChange={e => setRtspUrl(e.target.value)}
                  disabled={isStreaming}
                  placeholder="rtsp://username:password@192.168.1.100:554/stream"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 disabled:opacity-50 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Camera Name</label>
                <input
                  type="text"
                  value={cameraName}
                  onChange={e => setCameraName(e.target.value)}
                  disabled={isStreaming}
                  placeholder="Front Gate Camera"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 disabled:opacity-50"
                />
              </div>
            </div>
          )}
        </div>
        )}

        {/* Active classes */}
        {!isStreaming && (
        <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-2 text-sm text-gray-400">
          Detecting: <span className="text-cyan-400">{selectedObjects.join(', ')}</span>
          {!personEnabled && (
            <span className="mt-1 block text-amber-400 sm:ml-3 sm:mt-0 sm:inline">Queue & operator analytics require <code>person</code></span>
          )}
        </div>
        )}

        {/* Controls */}
        <div className={`${isStreaming ? 'mt-3' : 'mt-4'} flex gap-3 flex-wrap items-center`}>
          {!isStreaming ? (
            <button
              onClick={handleStart}
              disabled={loading}
              className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
            >
              {loading
                ? <><Loader className="w-4 h-4 animate-spin" /><span>Starting…</span></>
                : <><Play className="w-4 h-4" /><span>Start Detection</span></>}
            </button>
          ) : (
            <button onClick={handleStop} className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto">
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </button>
          )}
          {isStreaming && (
            <div className="min-w-0 text-sm text-gray-400">
              Source: <span className="text-cyan-300">{mode === MODE_RTSP ? 'RTSP Camera' : mode === MODE_MJPEG ? 'WiFi IP Camera' : mode === MODE_LOCAL_VIDEO ? 'Local Video File' : 'Webcam'}</span>
            </div>
          )}
        </div>

        {/* Status messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}
        {message && !error && (
          <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500 rounded-lg text-emerald-300 text-sm">
            {message}
          </div>
        )}
        {securityInsight && (
          <div className={`mt-4 p-3 border rounded-lg text-sm ${
            securityInsight.success === false
              ? 'bg-amber-500/10 border-amber-500 text-amber-200'
              : 'bg-sky-500/10 border-sky-500 text-sky-200'
          }`}>
            <p className="font-semibold mb-1">Groq activity review</p>
            <p className="whitespace-pre-line">
              {securityInsight.analysis || securityInsight.error || 'Activity logs sent for review.'}
            </p>
          </div>
        )}
      </div>

      {/* ── Live view + detection panel ── */}
      {(isStreaming || detections) && (
        <div className="stream-workspace grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-4">
          {/* Video / canvas feed */}
          <div className="glass-panel p-3 min-w-0">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-white">Live Feed</h3>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                {isStreaming && (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" />
                      LIVE
                    </span>
                    {processingFrame && <Loader className="w-3 h-3 animate-spin text-cyan-400" />}
                    <span>{stats.fps} det/s</span>
                  </>
                )}
              </div>
            </div>
            <div className="video-container stream-video-container relative overflow-hidden rounded-lg bg-black">
              {/* Webcam & Local Video: video element with canvas overlay */}
              {(mode === MODE_WEBCAM || mode === MODE_LOCAL_VIDEO) && (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    controls={mode === MODE_LOCAL_VIDEO}
                    className="video-frame"
                    onLoadedMetadata={handleWebcamMetadata}
                    onCanPlay={handleWebcamMetadata}
                  />
                  {!webcamReady && (
                    <div className="camera-status-overlay">
                      <Loader className="w-5 h-5 animate-spin text-cyan-300" />
                      <span>{mode === MODE_LOCAL_VIDEO ? 'Loading video file…' : 'Waiting for camera frames…'}</span>
                    </div>
                  )}
                  <canvas ref={overlayCanvasRef} className="detection-overlay" />
                </>
              )}
              {/* MJPEG: img element with canvas overlay */}
              {mode === MODE_MJPEG && (
                <div className="relative">
                  {backendFrame && (
                    <img
                      src={backendFrame}
                      alt="Processed WiFi camera stream"
                      className="video-frame"
                    />
                  )}
                </div>
              )}
              {mode === MODE_RTSP && backendFrame && (
                <img
                  src={backendFrame}
                  alt="Processed RTSP camera stream"
                  className="video-frame"
                />
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          </div>

          {/* Detection sidebar */}
          <div className="stream-sidebar space-y-3">
            {/* Stats */}
            <div className="glass-panel p-3">
              <h3 className="text-lg font-semibold text-white mb-3">Detections</h3>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Objects in frame</p>
                  <p className="text-2xl font-bold text-cyan-400">{detections?.count ?? 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Queue (people)</p>
                  <p className="text-2xl font-bold text-yellow-400">{personEnabled ? queueCount : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Frames processed</p>
                  <p className="text-xl font-semibold text-purple-400">{stats.frames}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Total detected</p>
                  <p className="text-xl font-semibold text-green-400">{stats.detectedTotal}</p>
                </div>
              </div>
            </div>

            {/* Detected classes */}
            {Object.keys(classCounts).length > 0 && (
              <div className="glass-panel p-3">
                <h3 className="text-lg font-semibold text-white mb-3">Detected Classes</h3>
                <div className="space-y-2">
                  {Object.entries(classCounts).map(([cls, count]) => (
                    <div key={cls} className="flex items-center justify-between rounded-lg bg-slate-800 px-3 py-2">
                      <span className="text-sm text-white capitalize">{cls}</span>
                      <span className="detection-badge">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-detection list */}
            {detections?.detections?.length > 0 && (
              <div className="glass-panel p-3">
                <h3 className="text-lg font-semibold text-white mb-3">All Detections</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {detections.detections.map((det, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-slate-800 px-3 py-2">
                      <span className="text-sm text-white capitalize">{det.class}</span>
                      <span className="text-xs text-cyan-400 font-semibold">
                        {(det.confidence * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Idle placeholder ── */}
      {!isStreaming && !detections && mode !== MODE_RTSP && (
        <div className="glass-panel p-6 text-center sm:p-12">
          <div className="inline-block p-4 bg-slate-800 rounded-full mb-4">
            {mode === MODE_WEBCAM
              ? <Monitor className="w-12 h-12 text-gray-400" />
              : mode === MODE_LOCAL_VIDEO
                ? <Video   className="w-12 h-12 text-gray-400" />
                : <Wifi    className="w-12 h-12 text-gray-400" />}
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {mode === MODE_WEBCAM ? 'Webcam Detection' : mode === MODE_LOCAL_VIDEO ? 'Local Video Detection' : 'WiFi Camera Detection'}
          </h3>
          <p className="text-gray-400 max-w-md mx-auto">
            {mode === MODE_WEBCAM
              ? 'Click "Start Detection" to begin. Your browser will ask for camera permission — allow it to start streaming.'
              : mode === MODE_LOCAL_VIDEO
                ? 'Select a local video file above and click "Start Detection". Your browser will play it and stream frames to the backend.'
                : 'Enter your WiFi camera\'s HTTP/MJPEG URL above and click "Start Detection". Your browser will fetch the stream and send frames to the backend for detection.'}
          </p>
          <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full bg-slate-800/60 px-3 py-1.5 text-xs text-gray-500">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
            <span className="min-w-0 truncate">Connected to {getApiBaseUrl()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
